import { useState } from "react";
import { Sun, Moon, Menu, Search, LogOut, User } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import { useAuth } from "./AuthProvider";
import { Button } from "./ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { Kbd } from "./ui/kbd";
import { DevModeBadge } from "@dolfin/ui";
import { Link } from "react-router-dom";
import { CommandMenu } from "./CommandMenu";
import { AdminSidebar } from "./AdminSidebar";

export function Header() {
  const { theme, toggleTheme } = useTheme();
  const { user, myUserId, logout } = useAuth();
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  return (
    <header className="z-40 flex h-12 shrink-0 items-center gap-2 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setNavOpen(true)} aria-label="Open navigation">
        <Menu className="h-5 w-5" />
      </Button>
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-56 p-0" showCloseButton={false}>
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <AdminSidebar onNavigate={() => setNavOpen(false)} className="border-r-0" />
        </SheetContent>
      </Sheet>

      <button
        onClick={() => setCommandMenuOpen(true)}
        className="inline-flex h-8 w-full max-w-72 items-center gap-2 rounded-md border border-input bg-muted/40 px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        <Search className="h-4 w-4" />
        <span className="hidden flex-1 text-left sm:inline">Search or jump to…</span>
        <Kbd>&#8984;K</Kbd>
      </button>

      <div className="flex-1" />

      {user?.devPersona && <DevModeBadge label={user.devPersona.label} />}

      <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label="Toggle theme">
        {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Avatar className="h-7 w-7 cursor-pointer">
              {user?.picture && <AvatarImage src={user.picture} alt={user?.name || "User"} />}
              <AvatarFallback className="text-xs">{user?.name?.[0]?.toUpperCase() || "U"}</AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.sub}</p>
          </div>
          <DropdownMenuSeparator />
          {myUserId && (
            <DropdownMenuItem asChild>
              <Link to={`/users/${myUserId}`}>
                <User className="mr-2 h-4 w-4" />
                My User
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={logout}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CommandMenu open={commandMenuOpen} onOpenChange={setCommandMenuOpen} />
    </header>
  );
}
